import React, { useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowRight, Check, ChevronLeft, ChevronRight, Sparkles,
  Briefcase, Rocket, Users, Building2, Layers, MessageSquare, Bot, PhoneCall,
  LineChart, Headphones, CalendarCheck, Search, Smartphone, Brain, PenTool,
} from "lucide-react";
import Aurora from "@/components/Aurora";
import Reveal from "@/components/Reveal";
import EnrollButton from "@/components/EnrollButton";
import { HERO, FINAL_CTA, SECTION } from "@/constants/testIds";

/* ============================================================
   EDIT ME — all copy that is likely to change lives up here.
   ============================================================ */

// Replace with real words from your comments/DMs — the closer to how your
// audience actually talks, the better this section converts.
const IDENTITY = [
  "You're stuck in a job that pays the bills but drains you.",
  "You don't have a real career yet, and you want to build one in AI.",
  "You want a high-income skill that could one day replace your paycheck.",
  "You've watched the tutorials and saved the reels, but you have nothing you could show a client.",
  "You want to work remotely, on your own terms, for yourself.",
  "You're already a freelancer, creator, or owner and want to be the one building the AI.",
];

const PATHS = [
  { icon: Rocket, title: "Start Your Own AI Agency", body: "Package agents as a service and sell them to local and online businesses." },
  { icon: Briefcase, title: "Become an AI Agent Freelancer", body: "Build agents for clients one project at a time, from anywhere." },
  { icon: Building2, title: "Get Hired as an AI Architect", body: "Companies need people who can actually build and deploy AI systems." },
  { icon: Layers, title: "Do Paid AI Builder Contracts", body: "Contract work building AI systems inside other companies." },
  { icon: Smartphone, title: "Build Your Own AI Agent App", body: "Turn a system you build into a product you own." },
  { icon: Users, title: "Offer AI Consulting", body: "Show businesses where AI replaces repetitive work, then build it." },
  { icon: Bot, title: "Build AI Employees Into Your Business", body: "Replace your own day-to-day manual work with agents that never clock out." },
];

// Illustrative pricing only. Put the numbers you actually charge / see charged.
const PRICE_EXAMPLES = [
  { system: "AI Content System", price: "$1,500 – $4,000", per: "per month" },
  { system: "AI Lead Generation System", price: "$2,000 – $5,000", per: "per month" },
  { system: "AI Voice Receptionist", price: "$1,000 – $3,000", per: "per month" },
  { system: "Custom AI Agent Build", price: "$2,000 – $10,000", per: "per project" },
];

// img: file slug in /public/systems (each has a -960.webp and a -1600.webp, both exact 16:9).
const SYSTEMS_CAROUSEL = [
  { title: "AI Client Acquisition System", icon: Search, img: "ai-client-acquisition-system" },
  { title: "AI Content Operating System", icon: Sparkles, img: "ai-content-operating-system" },
  { title: "AI Lead Generation Platform", icon: LineChart, img: "ai-lead-generation-platform" },
  { title: "AI Voice Receptionist", icon: PhoneCall, img: "ai-voice-receptionist" },
  { title: "AI Content Creation Platform", icon: PenTool, img: "ai-content-creation-platform" },
  { title: "AI Follow-Up & CRM Agent", icon: MessageSquare, img: "ai-follow-up-crm-agent" },
  { title: "AI Customer Support Agent", icon: Headphones, img: "ai-customer-support-agent" },
  { title: "AI Appointment Booking Agent", icon: CalendarCheck, img: "ai-appointment-booking-agent" },
  { title: "Your Own AI Agent App", icon: Brain, img: "ai-agent-app" },
];

const SYSTEM_IMG_SIZES = "(min-width: 640px) 440px, 86vw";
const systemSrcSet = (slug) => `/systems/${slug}-960.webp 960w, /systems/${slug}-1600.webp 1600w`;
const warmedSystemImages = []; // keeps references so the browser doesn't cancel the downloads

const TIERS = [
  {
    id: "core",
    name: "AI Agents Course",
    price: "$497",
    blurb: "Everything you need to build, deploy, and sell your first AI agents.",
    features: [
      "All 14 modules, start to finish",
      "Build your first agent in 1–2 days",
      "Dashboards, tracking & debugging lessons",
      "Bonus: Productize Your AI Agents (pricing, sales, delivery)",
      "Prompt templates & project blueprints",
      "Certification when you finish",
      "Instant access",
    ],
    cta: "START BUILDING — $497",
    packageId: undefined, // default package = entry price
  },
  {
    id: "pro",
    name: "Course + 1-on-1 Session",
    price: "$997",
    blurb: "The full course, plus a private 90-minute strategy and build session with me.",
    features: [
      "Everything in the AI Agents Course",
      "One 90-minute 1-on-1 session with Lovefavor",
      "Map your first offer, niche, and pricing",
      "Limited spots each month",
    ],
    cta: "GET THE COURSE + 1-ON-1 — $997",
    packageId: "ai_agents_course_pro_v1",
    featured: true,
  },
];

/* ============================================================
   Sections
   ============================================================ */

const H2 = "font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-5xl text-white";

export function Hero({ video }) {
  return (
    <section id="top" className="relative min-h-[100vh] flex flex-col justify-center overflow-hidden pt-28 pb-12">
      <Aurora variant="intense" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <Reveal>
          <div className="label-chip" style={{ whiteSpace: "nowrap", fontSize: "clamp(0.6rem, 2.3vw, 0.72rem)", padding: "0.5rem 1.05rem" }}>
            <span className="dot" /><span>ALWAYS ON SALES &middot; AI AGENTS COURSE</span>
          </div>
        </Reveal>

        <Reveal delay={100} x={-40} blur={8}>
          <h1 className="font-display text-white mt-8 text-[40px] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[84px] tracking-tighter max-w-5xl">
            <span className="font-normal">Learn to Build AI Agents &mdash; </span>
            <span className="font-bold">and Get Paid to Build Them for Businesses.</span>
          </h1>
        </Reveal>

        <Reveal delay={200}>
          <p className="mt-7 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed">
            Build your first AI agent in the first 1&ndash;2 days. Then turn the skill into your own
            AI agency, a freelance income, or a high-paying AI career &mdash; on your own terms.
          </p>
        </Reveal>

        <Reveal delay={300}>
          <div className="mt-10 max-w-3xl">{video}</div>
        </Reveal>

        <Reveal delay={350}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
            <EnrollButton testId={HERO.primaryCta} source="hero">START BUILDING &mdash; $497</EnrollButton>
            <button
              data-testid={HERO.secondaryCta}
              onClick={() => document.getElementById("systems")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-ghost"
            >
              SEE WHAT YOU&apos;LL BUILD
            </button>
          </div>
        </Reveal>
        <p className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500">No coding required &middot; Certification included &middot; Instant access</p>
      </div>
    </section>
  );
}

export function IdentitySection() {
  return (
    <section data-testid={SECTION.pain} className="relative py-24 sm:py-32 overflow-hidden bg-white">
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="label-chip-light"><span className="dot" /><span>WHY YOU&apos;RE HERE</span></div>
        </Reveal>
        <Reveal delay={100} x={-40} blur={6}>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-[#0a1930]">
            <span className="font-normal">You&apos;re Not Learning AI </span>
            <span className="font-bold">Just for Fun.</span>
          </h2>
        </Reveal>
        <Reveal delay={150}>
          <p className="mt-6 text-gray-600 text-lg leading-relaxed max-w-3xl">
            You&apos;re intrigued by AI, but curiosity isn&apos;t why you&apos;re reading this. You want the
            freedom of building on your own terms. If this sounds like you:
          </p>
        </Reveal>
        <ul className="mt-10 space-y-4 max-w-3xl">
          {IDENTITY.map((t, i) => (
            <Reveal key={i} delay={40 * i}>
              <li className="flex items-start gap-3 text-[#0a1930] text-base sm:text-lg leading-snug">
                <Check className="w-5 h-5 text-[#0055ff] mt-1 shrink-0" strokeWidth={2.5} />
                <span>{t}</span>
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={200}>
          <p className="mt-10 text-[#0a1930] text-lg font-semibold max-w-3xl">
            Then this skill was made for you: building AI agents that businesses will pay for.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function StuckSection() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden">
      <Aurora variant="intense" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="label-chip"><span className="dot" /><span>THE COST OF STAYING THE SAME</span></div>
        </Reveal>
        <Reveal delay={100} x={-40} blur={6}>
          <h2 className={H2}>
            <span className="font-normal">Another Year Like This One </span>
            <span className="font-bold">Isn&apos;t a Plan.</span>
          </h2>
        </Reveal>
        <div className="mt-10 space-y-5 text-zinc-300 text-base sm:text-lg leading-relaxed max-w-3xl">
          <Reveal delay={150}><p>Staying where you are feels safe. It isn&apos;t. It&apos;s the same job, the same ceiling, and the same &ldquo;one day&rdquo; you keep telling yourself.</p></Reveal>
          <Reveal delay={200}><p>Meanwhile you keep consuming AI content and still have nothing to show for it. No system you built. No client you could point to. No proof you can do this.</p></Reveal>
          <Reveal delay={250}><p><span className="text-white">There&apos;s a difference between someone who&apos;s &ldquo;into AI&rdquo; and someone who builds and sells it.</span> The only thing between them is a skill and a first project.</p></Reveal>
        </div>

        <Reveal delay={150}>
          <div className="mt-14 glass-card glow-cyan p-7 sm:p-10">
            <h3 className="font-display text-white text-2xl sm:text-3xl leading-tight">
              <span className="font-normal">Learn the Skill. </span><span className="font-bold">Stop Fearing the Tool.</span>
            </h3>
            <p className="mt-4 text-zinc-300 leading-relaxed">
              Learning a high-income AI skill in this digital economy is one of the best ways to protect your
              financial future and your digital future, because you stop fearing something you know how to use.
              That&apos;s if you implement what&apos;s inside this course.
            </p>
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className="mt-8 p-7 sm:p-10 rounded-2xl border border-white/10">
            <div className="text-xs tracking-[0.3em] uppercase text-cyan-300">Why now</div>
            <p className="mt-4 text-zinc-300 leading-relaxed text-base sm:text-lg">
              AI changes every single day, and that&apos;s not an exaggeration. The longer you wait to monetize
              this skill, the harder it gets to compete. The window to learn to build AI systems is still wide
              open, but I can&apos;t promise it stays that way.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function PathsSection() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-white">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><div className="label-chip-light"><span className="dot" /><span>WHAT THIS SKILL OPENS UP</span></div></Reveal>
        <Reveal delay={100} x={-40} blur={6}>
          <h2 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-5xl text-[#0a1930]">
            <span className="font-normal">One Skill. </span><span className="font-bold">Seven Ways to Get Paid.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PATHS.map((p, i) => (
            <Reveal key={i} delay={40 * i}>
              <div className="glass-card-light p-6 h-full">
                <p.icon className="w-6 h-6 text-[#0055ff]" />
                <h3 className="mt-4 font-display text-[#0a1930] text-xl font-bold leading-snug">{p.title}</h3>
                <p className="mt-2 text-gray-600 text-sm sm:text-base leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={100}>
          <h3 className="mt-20 font-display text-[#0a1930] text-2xl sm:text-4xl tracking-tight">
            <span className="font-normal">What AI Systems Are </span><span className="font-bold">Priced At</span>
          </h3>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PRICE_EXAMPLES.map((p, i) => (
            <Reveal key={i} delay={40 * i}>
              <div className="rounded-2xl bg-[#0a1930] text-white p-6 h-full">
                <div className="text-sm text-cyan-300">{p.system}</div>
                <div className="mt-3 font-display text-2xl sm:text-3xl font-bold">{p.price}</div>
                <div className="text-xs tracking-[0.2em] uppercase text-zinc-400 mt-1">{p.per}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-5 text-xs text-gray-500 max-w-2xl">
          Illustrative pricing for AI systems, not an earnings guarantee. What you earn depends on the work you do and the clients you win.
        </p>
      </div>
    </section>
  );
}

export function SystemsCarousel() {
  const ref = useRef(null);
  // Quietly pre-fetch every system image once the page has finished loading, so the cards
  // are already cached by the time someone scrolls here (without slowing down first paint).
  useEffect(() => {
    let cancelled = false;
    let timer;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 120));
    const warm = () => {
      SYSTEMS_CAROUSEL.forEach((sys) => {
        idle(() => {
          if (cancelled) return;
          const im = new Image();
          im.decoding = "async";
          im.sizes = SYSTEM_IMG_SIZES;
          im.srcset = systemSrcSet(sys.img);
          im.src = `/systems/${sys.img}-960.webp`;
          warmedSystemImages.push(im);
        });
      });
    };
    const start = () => { timer = setTimeout(warm, 2500); };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => { cancelled = true; clearTimeout(timer); window.removeEventListener("load", start); };
  }, []);
  const scroll = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };
  return (
    <section id="systems" className="relative py-24 sm:py-32 overflow-hidden">
      <Aurora variant="intense" rings />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><div className="label-chip"><span className="dot" /><span>SYSTEMS YOU&apos;LL BE ABLE TO BUILD</span></div></Reveal>
        <div className="flex items-end justify-between gap-6">
          <Reveal delay={100} x={-40} blur={6}>
            <h2 className={H2}>
              <span className="font-normal">Real Systems. </span><span className="font-bold">Built From This Course.</span>
            </h2>
          </Reveal>
          <div className="hidden sm:flex gap-2 shrink-0">
            <button aria-label="Previous" onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-white/20 grid place-items-center text-white hover:border-cyan-400"><ChevronLeft className="w-5 h-5" /></button>
            <button aria-label="Next" onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-white/20 grid place-items-center text-white hover:border-cyan-400"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </div>
        <Reveal delay={150}>
          <p className="mt-5 text-zinc-400 max-w-2xl">You can start building within the first module. Swipe through what you&apos;ll be able to create.</p>
        </Reveal>

        <div
          ref={ref}
          className="mt-10 flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 sm:mx-0 sm:px-0"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {SYSTEMS_CAROUSEL.map((s, i) => (
            <article key={i} data-testid={`system-card-${i}`} className="snap-center shrink-0 w-[86%] sm:w-[440px] rounded-3xl overflow-hidden glass-card">
              <div className="relative aspect-video bg-[#0a0f1f]">
                {s.img ? (
                  <img
                    src={`/systems/${s.img}-960.webp`}
                    srcSet={systemSrcSet(s.img)}
                    sizes={SYSTEM_IMG_SIZES}
                    width={1600}
                    height={900}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-cyan-500/20 via-blue-700/20 to-black"><s.icon className="w-16 h-16 text-cyan-300/70" strokeWidth={1.25} /></div>
                )}
              </div>
              <h3 className="p-5 font-display text-white text-lg font-bold leading-snug">{s.title}</h3>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TiersSection() {
  return (
    <section id="enroll" className="relative py-24 sm:py-32 overflow-hidden bg-white" data-testid={SECTION.finalCta}>
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal x={-40} blur={8}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] text-[#0a1930] text-center">
            <span className="font-normal">Build Your First AI Agent </span><span className="font-bold text-[#0055ff]">This Week.</span>
          </h2>
        </Reveal>
        <Reveal delay={100}>
          <p className="mt-6 text-gray-600 text-lg text-center max-w-2xl mx-auto">
            One-time payment. No subscription. Pick the way you want to start.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={100 * i}>
              <div className={`h-full rounded-3xl p-8 sm:p-10 flex flex-col ${t.featured ? "bg-[#0a1930] text-white shadow-2xl" : "bg-gray-50 border border-gray-200 text-[#0a1930]"}`}>
                <div className={`text-xs tracking-[0.3em] uppercase ${t.featured ? "text-cyan-300" : "text-[#0055ff]"}`}>{t.name}</div>
                <div className="mt-4 font-display text-6xl font-bold">{t.price}</div>
                <p className={`mt-3 ${t.featured ? "text-zinc-300" : "text-gray-600"}`}>{t.blurb}</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm sm:text-base">
                      <Check className={`w-4 h-4 mt-1 shrink-0 ${t.featured ? "text-cyan-300" : "text-[#0055ff]"}`} strokeWidth={3} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <EnrollButton
                    testId={t.featured ? "tier-pro-enroll" : FINAL_CTA.enroll}
                    source={`tier_${t.id}`}
                    packageId={t.packageId}
                    className="btn-aurora !py-4 !px-7 w-full justify-center"
                  >
                    {t.cta}
                  </EnrollButton>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function QuizSection() {
  return (
    <section className="relative py-20 overflow-hidden">
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
            <span className="font-normal">Not Sure This Is </span><span className="font-bold">Right for You?</span>
          </h2>
          <p className="mt-4 text-zinc-400">Take the free 2-minute quiz and see if the AI Agents Course is the right fit.</p>
          <RouterLink to="/assessment" className="btn-ghost mt-8 inline-flex items-center gap-2">
            TAKE THE QUIZ <ArrowRight className="w-4 h-4" />
          </RouterLink>
        </Reveal>
      </div>
    </section>
  );
}
